import { supabase } from "@/integrations/supabase/client";

/**
 * Auto WhatsApp triggers — silent background calls to edge functions.
 * Mirrors auto-email-triggers.ts pattern. Fire-and-forget, never disrupts main flow.
 */

/**
 * Triggers WhatsApp notification for appointment events (created/rescheduled/cancelled).
 * Calls the send-appointment-whatsapp edge function.
 */
export async function triggerAppointmentWhatsApp(
  appointmentId: string,
  notificationType: 'created' | 'rescheduled' | 'cancelled'
): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('send-appointment-whatsapp', {
      body: {
        appointmentId,
        notificationType,
      },
    });

    if (error) {
      console.error('[triggerAppointmentWhatsApp] Edge function error:', error);
      return;
    }

    if (data?.success) {
      console.log(`[triggerAppointmentWhatsApp] Successfully sent ${notificationType} WhatsApp`);
    } else if (data?.message) {
      console.log(`[triggerAppointmentWhatsApp] Not sent: ${data.message}`);
    }
  } catch (err) {
    console.error('[triggerAppointmentWhatsApp] Failed:', err);
  }
}

/**
 * Triggers WhatsApp notification when a bill is created.
 * Calls the send-document-whatsapp edge function with type 'bill_share'.
 */
export async function triggerBillCreatedWhatsApp(
  clinicId: string,
  patientId: string,
  billId: string
): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('send-document-whatsapp', {
      body: {
        documentType: 'bill_share',
        patientId,
        clinicId,
        billId,
      },
    });

    if (error) {
      console.error('[triggerBillCreatedWhatsApp] Edge function error:', error);
      return;
    }

    if (data?.success) {
      console.log('[triggerBillCreatedWhatsApp] Successfully sent bill WhatsApp');
    } else if (data?.message) {
      console.log(`[triggerBillCreatedWhatsApp] Not sent: ${data.message}`);
    }
  } catch (err) {
    console.error('[triggerBillCreatedWhatsApp] Failed:', err);
  }
}

/**
 * Triggers WhatsApp notification when a payment is received.
 * Calls send-whatsapp-message with a payment confirmation template.
 */
export async function triggerPaymentReceivedWhatsApp(
  clinicId: string,
  patientId: string,
  patientPhone: string,
  patientName: string,
  amount: number,
  billId: string
): Promise<void> {
  try {
    // Fetch whatsapp config to check if enabled
    const { data: configs } = await supabase
      .from("whatsapp_notification_config")
      .select("is_enabled, send_on_bill_share, template_id_bill_share, template_language")
      .or(`clinic_id.eq.${clinicId},clinic_id.is.null`)
      .order("clinic_id", { ascending: false, nullsFirst: false })
      .limit(1);

    const config = configs?.[0];
    if (!config?.is_enabled || !config?.send_on_bill_share) {
      console.log('[triggerPaymentReceivedWhatsApp] WhatsApp notifications disabled for this clinic');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase.functions.invoke('send-whatsapp-message', {
      body: {
        templateName: config.template_id_bill_share || 'payment_received',
        to: patientPhone,
        variables: [patientName, `₹${amount}`],
        languageCode: config.template_language || 'en',
        clinicId,
        patientId,
        communicationType: 'payment_received',
        recipientName: patientName,
        messageContent: `Payment of ₹${amount} received`,
        sentBy: user?.id,
      },
    });

    if (error) {
      console.error('[triggerPaymentReceivedWhatsApp] Edge function error:', error);
      return;
    }

    if (data?.success) {
      console.log('[triggerPaymentReceivedWhatsApp] Successfully sent payment WhatsApp');
    }
  } catch (err) {
    console.error('[triggerPaymentReceivedWhatsApp] Failed:', err);
  }
}

/**
 * Triggers WhatsApp notification when an X-ray is uploaded in the imaging queue.
 * Calls the send-document-whatsapp edge function with type 'xray_share'.
 */
export async function triggerXraySharedWhatsApp(
  clinicId: string,
  patientId: string,
  attachmentId: string
): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('send-document-whatsapp', {
      body: {
        documentType: 'xray_share',
        patientId,
        clinicId,
        attachmentId,
      },
    });

    if (error) {
      console.error('[triggerXraySharedWhatsApp] Edge function error:', error);
      return;
    }

    if (data?.success) {
      console.log('[triggerXraySharedWhatsApp] Successfully sent X-ray WhatsApp');
    } else if (data?.message) {
      console.log(`[triggerXraySharedWhatsApp] Not sent: ${data.message}`);
    }
  } catch (err) {
    console.error('[triggerXraySharedWhatsApp] Failed:', err);
  }
}

/**
 * Triggers WhatsApp notification when a prescription is created.
 * Calls the send-document-whatsapp edge function with type 'prescription_share'.
 */
export async function triggerPrescriptionSharedWhatsApp(
  clinicId: string,
  patientId: string,
  prescriptionId: string
): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('send-document-whatsapp', {
      body: {
        documentType: 'prescription_share',
        patientId,
        clinicId,
        prescriptionId,
      },
    });

    if (error) {
      console.error('[triggerPrescriptionSharedWhatsApp] Edge function error:', error);
      return;
    }

    if (data?.success) {
      console.log('[triggerPrescriptionSharedWhatsApp] Successfully sent prescription WhatsApp');
    } else if (data?.message) {
      console.log(`[triggerPrescriptionSharedWhatsApp] Not sent: ${data.message}`);
    }
  } catch (err) {
    console.error('[triggerPrescriptionSharedWhatsApp] Failed:', err);
  }
}

/**
 * Triggers WhatsApp notification when a clinical visit is completed.
 * Calls send-whatsapp-message with a visit completed template.
 */
export async function triggerVisitCompletedWhatsApp(
  clinicId: string,
  patientId: string,
  patientPhone: string,
  patientName: string
): Promise<void> {
  try {
    // Fetch whatsapp config to check if enabled
    const { data: configs } = await supabase
      .from("whatsapp_notification_config")
      .select("is_enabled, template_language")
      .or(`clinic_id.eq.${clinicId},clinic_id.is.null`)
      .order("clinic_id", { ascending: false, nullsFirst: false })
      .limit(1);

    const config = configs?.[0];
    if (!config?.is_enabled) {
      console.log('[triggerVisitCompletedWhatsApp] WhatsApp notifications disabled');
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();

    const { data, error } = await supabase.functions.invoke('send-whatsapp-message', {
      body: {
        templateName: 'visit_completed',
        to: patientPhone,
        variables: [patientName],
        languageCode: config.template_language || 'en',
        clinicId,
        patientId,
        communicationType: 'visit_completed',
        recipientName: patientName,
        messageContent: 'Visit completed notification',
        sentBy: user?.id,
      },
    });

    if (error) {
      console.error('[triggerVisitCompletedWhatsApp] Edge function error:', error);
      return;
    }

    if (data?.success) {
    console.log('[triggerVisitCompletedWhatsApp] Successfully sent visit completed WhatsApp');
    }
  } catch (err) {
    console.error('[triggerVisitCompletedWhatsApp] Failed:', err);
  }
}

/**
 * Triggers WhatsApp notification to the doctor when a new appointment is booked.
 * Calls the send-doctor-whatsapp edge function with type 'new_appointment'.
 */
export async function triggerDoctorNewAppointmentWhatsApp(
  appointmentId: string
): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('send-doctor-whatsapp', {
      body: {
        appointmentId,
        notificationType: 'new_appointment',
      },
    });

    if (error) {
      console.error('[triggerDoctorNewAppointmentWhatsApp] Edge function error:', error);
      return;
    }

    if (data?.success) {
      console.log('[triggerDoctorNewAppointmentWhatsApp] Successfully sent doctor new appointment WhatsApp');
    } else if (data?.message) {
      console.log(`[triggerDoctorNewAppointmentWhatsApp] Not sent: ${data.message}`);
    }
  } catch (err) {
    console.error('[triggerDoctorNewAppointmentWhatsApp] Failed:', err);
  }
}

/**
 * Triggers WhatsApp notification to the doctor when their appointment is cancelled.
 * Calls the send-doctor-whatsapp edge function with type 'cancellation'.
 */
export async function triggerDoctorCancellationWhatsApp(
  appointmentId: string
): Promise<void> {
  try {
    const { data, error } = await supabase.functions.invoke('send-doctor-whatsapp', {
      body: {
        appointmentId,
        notificationType: 'cancellation',
      },
    });

    if (error) {
      console.error('[triggerDoctorCancellationWhatsApp] Edge function error:', error);
      return;
    }

    if (data?.success) {
      console.log('[triggerDoctorCancellationWhatsApp] Successfully sent doctor cancellation WhatsApp');
    } else if (data?.message) {
      console.log(`[triggerDoctorCancellationWhatsApp] Not sent: ${data.message}`);
    }
  } catch (err) {
    console.error('[triggerDoctorCancellationWhatsApp] Failed:', err);
  }
}
